import React, { useState, useEffect } from 'react';
import { FileText, Save } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';

export default function AdminStaticPages() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const data = await api.getStaticPages();
      setPages(data);
      if (data.length > 0) {
        setSelectedPage(data[0]);
        setTitle(data[0].title);
        setContent(data[0].content);
      }
    } catch (err) {
      console.error('Failed to load pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
    setTitle(page.title);
    setContent(page.content);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    setSaving(true);
    setSuccess(false);
    try {
      await api.updateStaticPage(selectedPage.slug, { title, content });
      setSuccess(true);
      // Reload pages to get updated data
      await loadPages();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save page:', err);
      alert('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="admin-view space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Static Pages</h2>
          <p className="text-gray-500 text-sm">Edit content for About Us, Privacy Policy, and Terms & Conditions</p>
        </div>
      </div>

      <div className="admin-static-pages-layout grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Page selector */}
        <div className="admin-static-page-nav lg:col-span-1">
          {pages.map(page => (
            <button
              key={page.id}
              onClick={() => handlePageSelect(page)}
              className={`admin-static-page-link ${selectedPage?.id === page.id ? 'is-active' : ''}`}
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{page.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">/{page.slug}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Content editor */}
        <div className="lg:col-span-3">
          {selectedPage && (
            <div className="admin-static-page-editor bg-white rounded-2xl border border-gray-200 p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">Page Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">
                    Page Content
                    <span className="ml-2 font-normal text-gray-400">(HTML supported)</span>
                  </label>
                  <textarea
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    rows={16}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono resize-none"
                    placeholder="Enter page content here. You can use HTML tags like <p>, <h1>, <h2>, <ul>, <li>, etc."
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Tip: Use HTML tags for formatting. Example: &lt;p&gt;Your text here&lt;/p&gt;
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  {success && (
                    <span className="text-green-600 text-sm font-medium">✓ Saved successfully!</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
